import React, {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import parseHTML from 'html-react-parser';
import {SVVInformationMessage} from "@komponentkassen/svv-information-message";
import api from "../../helpers/api";
import {useApptekster} from "../../apptekst-context";
import {SVVButton, SVVButtonRow} from "@komponentkassen/svv-button";
import {SVVTextInput} from "@komponentkassen/svv-form";
import {isValidEmail, isValidPhoneNumber, trimPhoneNumber} from "../../helpers/InputValidators";
import {SVVBodyText, SVVHeading} from "@komponentkassen/svv-text";
import ResendBooking from "../reconfirm/ResendBooking";

const GetBooking = (props: {
  setBooking: any,
  isRebook: boolean
}) => {
  const t = useApptekster();
  const navigate = useNavigate();
  const [bookingNumber, setBookingNumber] = useState<string>('');
  const [emailOrPhoneNumber, setEmailOrPhoneNumber] = useState<string>('');
  const [fetchingError, setFetchingError] = useState<boolean>(false);
  const [unauthorizedError, setUnauthorizedError] = useState<boolean>(false);
  const [triedToSubmit, setTriedToSubmit] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const bookingNumberValid: boolean = bookingNumber.trim() !== '';
  const emailOrPhoneValid: boolean = emailOrPhoneNumber.includes('@') ? isValidEmail(emailOrPhoneNumber) : isValidPhoneNumber(trimPhoneNumber(emailOrPhoneNumber)) && emailOrPhoneNumber !== '';

  const headingRef = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    if (headingRef?.current) {
      headingRef.current.focus()
    }
  }, [headingRef])

  const submit = (event: any) => {
    event.preventDefault();
    setTriedToSubmit(true);

    if (bookingNumber && emailOrPhoneValid) {
      setFetchingError(false);
      setUnauthorizedError(false);
      setIsFetching(true);
      api
        .get(
          `/api/Timebestilling/booking?bookingNumber=${bookingNumber}&emailOrPhone=${trimPhoneNumber(emailOrPhoneNumber)}`
        )
        .then((response) => {
          if (response.data && response.data.bookingStatusId === 2) {
            props.setBooking(response.data);
            const url: string = props.isRebook ? '/endre/bekrefte' : '/avbestill/bekreftelse';
            navigate(url);
          } else {
            setFetchingError(true);
            return false;
          }
          setIsFetching(false);
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            setUnauthorizedError(true)
          } else {
            setFetchingError(true);
          }
          setIsFetching(false);
          // handle error
        });
    } else {
      setIsFetching(false);
      return false;
    }
  };
  
  return (
    <>
      <form onSubmit={submit} noValidate>
        <SVVHeading
          level="h2"
          ref={headingRef}
          tabIndex={-1}
        >
          {t("timebestilling.skriv-inn-referansenummer")}
        </SVVHeading>
        <SVVBodyText className="spacing-bottom--small svv-strong">
          {t("timebestilling.fyll-ut-felter")}
        </SVVBodyText>
        <div className="spacing-bottom--small">
          <SVVTextInput 
            label={t("timebestilling.referansenummer")}
            id="referansenummer"
            value={bookingNumber}
            onChange={e => setBookingNumber(e.target.value)}
            errorMessage={!bookingNumberValid && triedToSubmit ? t("timebestilling.du-ma-fylle-inn-refnr") : ""}
            inputSize="large"
            autoComplete="off"
            required
          />
        </div>
        <div className="spacing-bottom--small">
          <SVVTextInput 
            label={t("timebestilling.epost-eller-mob")}
            id="emailOrPhone"
            onChange={e => setEmailOrPhoneNumber(e.target.value)}
            inputSize="large"
            errorMessage={!emailOrPhoneValid && triedToSubmit ? t("timebestilling.fyll-inn-epost-eller-mob") : ""}
            value={emailOrPhoneNumber}
            required
          />
        </div>

        <div aria-live="assertive">
          {fetchingError && (
            <SVVInformationMessage 
              type="error"
              className="spacing-bottom--small"
            >
              <p>
                {t("timebestilling.kunne-ikke-finne-time")}
              </p>
            </SVVInformationMessage>
          )}
          {unauthorizedError && (
            <SVVInformationMessage
              type="error"
              className="spacing-bottom--small"
            >
              <p>
                {parseHTML(t("timebestilling.ikke-autorisert"))}
              </p>
            </SVVInformationMessage>
          )}
        </div>
        
        <SVVButtonRow>
          <SVVButton
            type={"submit"}
            disabled={isFetching}
            loading={isFetching}
          >
            {t("timebestilling.hent-time")}
          </SVVButton>
        </SVVButtonRow>
      </form>
      
      <ResendBooking/>
    </>
  );
};

export default GetBooking;
